import Footer from '@components/organisms/FooterNew'
import Edugram500 from '@components/pages/advantageous/Edugram500'
import Explore from '@components/pages/advantageous/Explore'
import PublishCourse from '@components/pages/advantageous/PublishCourse'
import Testimonials from '@components/pages/advantageous/Testimonials'
import UseCaseFAQSection from '@components/pages/use-cases/UseCaseFAQSection'
import UseCaseHeroSection from '@components/pages/use-cases/UseCaseHeroSection'
import AuthorDetailsModal from '@components/pages/become-author/AuthorDetailsModal'
import Header from '@components/pages/home/LandingPage/Sections/Header'
import MustExistSection, {
  Tab,
} from '@components/pages/home/LandingPage/Sections/MustExist'
import SafeContent from '@components/pages/home/LandingPage/Sections/SafeContent'
import TrustedStudents from '@components/pages/home/LandingPage/Sections/TrustedStudents'
import About from '@components/pages/resources/online-courses/About'
import { useAuth } from '@contexts/Auth'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Spacer } from '@nextui-org/react-latest'
import { getUseCaseSlugs, getUseCaseBySlug } from '@data/useCases'
import { GetStaticPaths, GetStaticProps } from 'next'
import withLenis from '@components/HOCs/LenisWrapper'

type Props = {
  useCaseSlug: string
}

const UseCasePage = ({ useCaseSlug }: Props) => {
  const [showBecomeAuthorModal, setShowBecomeAuthorModal] = useState(false)
  const { user, isAuthenticated, setShowAuthModal } = useAuth()
  const freshLoggedIn = useRef(false)
  const router = useRouter()
  const { t: tUseCase, i18n } = useTranslation('useCases')

  const useCase = getUseCaseBySlug(useCaseSlug)

  useEffect(() => {
    if (user && !user?.isAuthor && freshLoggedIn.current) {
      setShowBecomeAuthorModal(true)
    }

    return () => {
      freshLoggedIn.current = false
    }
  }, [user])

  const handleBecomeCreator = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true)
      freshLoggedIn.current = true
      return
    }
    if (user?.isAuthor) router.push('/author')
    else setShowBecomeAuthorModal(true)
  }

  // Load feature tabs from translations
  const featureTabs: Tab[] = Array.from({ length: 6 }, (_, i) => ({
    label: tUseCase(`${useCaseSlug}.features.${i}.label`),
    title: tUseCase(`${useCaseSlug}.features.${i}.title`),
    description: tUseCase(`${useCaseSlug}.features.${i}.description`),
    image: tUseCase(`${useCaseSlug}.features.${i}.image`),
    alt: tUseCase(`${useCaseSlug}.features.${i}.alt`),
  }))

  // Load benefit sections
  const benefitSections = Array.from({ length: 3 }, (_, i) => ({
    title: tUseCase(`${useCaseSlug}.benefits.sections.${i}.title`),
    description: tUseCase(`${useCaseSlug}.benefits.sections.${i}.description`),
  }))

  if (!useCase) {
    return null
  }

  return (
    <div>
      <Head>
        <title>{tUseCase(`${useCaseSlug}.seo.title`)}</title>
        <meta name="og:title" content={tUseCase(`${useCaseSlug}.seo.title`)} />
        <meta
          name="description"
          content={tUseCase(`${useCaseSlug}.seo.description`)}
        />
        <meta
          name="og:description"
          content={tUseCase(`${useCaseSlug}.seo.description`)}
        />
        <link
          rel="canonical"
          href={`https://www.edugram.io/use-cases/${useCaseSlug}/`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Edugram.io" />
        <meta
          name="twitter:description"
          content={tUseCase(`${useCaseSlug}.seo.description`)}
        />
        <meta
          property="og:image"
          content={`/static/images/og_images/use-cases/${useCaseSlug}.png`}
        />
        <meta
          property="og:image:secure_url"
          content={`/static/images/og_images/use-cases/${useCaseSlug}.png`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Edugram.io" />
        <meta property="og:locale" content={i18n.language} />
        <meta name="twitter:domain" content="www.edugram.io" />
        <meta name="keywords" content={useCase.keywords.join(', ')} />
      </Head>
      <Header handleBecomeCreator={handleBecomeCreator} />
      <UseCaseHeroSection
        handleBecomeCreator={handleBecomeCreator}
        title={tUseCase(`${useCaseSlug}.hero.title`)}
        description={tUseCase(`${useCaseSlug}.hero.description`)}
      />
      <About
        customSections={benefitSections}
        mainTitle={tUseCase(`${useCaseSlug}.benefits.mainTitle`)}
        mainDescription={tUseCase(`${useCaseSlug}.benefits.mainDescription`)}
      />
      <MustExistSection
        initialTabs={featureTabs}
        title={tUseCase(`${useCaseSlug}.features.sectionTitle`)}
        containerClassname="rounded-t-none"
      />
      <Spacer y={24} />
      <PublishCourse
        handleBecomeCreator={handleBecomeCreator}
        isHomeLanding={true}
      />
      <Edugram500 />
      <SafeContent />
      <TrustedStudents handleBecomeCreator={handleBecomeCreator} />
      <Testimonials />
      <UseCaseFAQSection useCaseSlug={useCaseSlug} />
      <Explore />
      <div className="max-w-desktop mx-auto sm:px-4">
        <Footer />
      </div>
      {showBecomeAuthorModal && (
        <AuthorDetailsModal
          isOpen={showBecomeAuthorModal}
          onClose={(ask?: boolean) => {
            if (ask) {
              if (confirm('Are you sure you want to close?')) {
                setShowBecomeAuthorModal((prev) => !prev)
              }
            } else {
              setShowBecomeAuthorModal((prev) => !prev)
            }
          }}
        />
      )}
    </div>
  )
}

export default withLenis(UseCasePage)

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const useCaseSlugs = getUseCaseSlugs()
  const paths: { params: { page: string }; locale: string }[] = []

  for (const locale of locales || ['en']) {
    for (const slug of useCaseSlugs) {
      paths.push({
        params: { page: slug },
        locale,
      })
    }
  }

  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const useCaseSlug = params?.page as string

  return {
    props: {
      useCaseSlug,
      ...(await serverSideTranslations(locale || 'en', [
        'common',
        'authentication',
        'footer',
        'courses',
        'signUp',
        'home',
        'wishlistDropdown',
        'errors',
        'advantageous',
        'homeLanding/header',
        'homeLanding/safecontent',
        'homeLanding/trustedstudents',
        'becomeAuthorDetailsModal',
        'useCases',
        'reportProblem',
      ])),
    },
  }
}
